import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "motion/react";
import { ArrowLeft, ArrowRight, Clock, CheckCircle2 } from "lucide-react";
import { blogPosts } from "../data/blog";
import type { BlogPost } from "../data/blog";

function FadeIn({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-2xl md:text-3xl font-bold text-white font-Montserrat mb-6 flex items-center gap-3">
      <span className="w-1 h-7 rounded-full bg-indigo-500 shrink-0" />
      {children}
    </h2>
  );
}

function WhatIsSection({
  what_is,
}: {
  what_is: NonNullable<BlogPost["what_is"]>;
}) {
  return (
    <FadeIn>
      <SectionHeading>{what_is.title}</SectionHeading>
      <p className="text-base md:text-lg text-white/70 font-Montserrat font-normal leading-relaxed mb-6 text-justify">
        {what_is.description}
      </p>

      {(what_is.display_types || what_is.components) && (
        <div className="grid grid-cols-2 gap-3 mb-6">
          {(what_is.display_types ?? what_is.components ?? []).map(
            (item, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.03]"
              >
                <CheckCircle2 size={14} className="text-indigo-400 shrink-0" />
                <span className="text-sm text-white/80 font-Montserrat">
                  {item}
                </span>
              </div>
            ),
          )}
        </div>
      )}

      {what_is.key_takeaway && (
        <div className="p-4 md:p-5 rounded-xl border-l-2 border-indigo-500/60 bg-indigo-500/5 mb-8">
          <p className="text-sm md:text-base text-white/80 font-Montserrat leading-relaxed text-justify">
            {what_is.key_takeaway}
          </p>
        </div>
      )}
    </FadeIn>
  );
}

function BenefitsSection({
  benefits,
}: {
  benefits: NonNullable<BlogPost["benefits"]>;
}) {
  return (
    <FadeIn>
      <SectionHeading>{benefits.title}</SectionHeading>
      <p className="text-base md:text-lg text-white/70 font-Montserrat font-normal leading-relaxed mb-8 text-justify">
        {benefits.intro}
      </p>
      <div className="space-y-6">
        {benefits.items.map((item) => (
          <motion.div
            key={item.number}
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{
              duration: 0.5,
              ease: [0.16, 1, 0.3, 1],
              delay: item.number * 0.08,
            }}
            className="flex gap-4 p-5 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-indigo-500/20 transition-all duration-500"
          >
            <span className="text-indigo-400 font-bold text-sm mt-0.5 shrink-0 font-Montserrat tabular-nums">
              {String(item.number).padStart(2, "0")}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-3 flex-wrap">
                <h4 className="text-white font-Montserrat font-semibold text-base leading-snug">
                  {item.title}
                </h4>
                {item.stat && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-indigo-500/15 border border-indigo-500/25 text-indigo-400 text-[11px] font-Montserrat font-semibold whitespace-nowrap">
                    {item.stat.value}
                  </span>
                )}
              </div>
              <p className="text-white/60 font-Montserrat font-normal text-sm mt-1.5 leading-relaxed text-justify">
                {item.description}
              </p>
              {item.stat && (
                <p className="text-indigo-400/60 font-Montserrat font-normal text-xs mt-1 text-justify">
                  {item.stat.context}
                </p>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </FadeIn>
  );
}

function WhyInvestSection({
  why_invest,
}: {
  why_invest: NonNullable<BlogPost["why_invest"]>;
}) {
  return (
    <FadeIn>
      <SectionHeading>{why_invest.title}</SectionHeading>
      <p className="text-base md:text-lg text-white/70 font-Montserrat font-normal leading-relaxed mb-8 text-justify">
        {why_invest.intro}
      </p>

      {why_invest.stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {why_invest.stats.map((stat, i) => (
            <div
              key={i}
              className="text-center p-5 rounded-xl border border-white/10 bg-white/[0.03]"
            >
              <div className="text-3xl md:text-4xl font-bold text-indigo-400 font-Montserrat mb-1">
                {stat.value}
              </div>
              <div className="text-xs font-Montserrat uppercase tracking-widest text-white/40 mb-2">
                {stat.metric}
              </div>
              <p className="text-sm text-white/60 font-Montserrat font-normal leading-relaxed text-justify">
                {stat.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {why_invest.key_advantages && (
        <div className="space-y-6">
          {why_invest.key_advantages.map((adv, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.5,
                ease: [0.16, 1, 0.3, 1],
                delay: (i + 1) * 0.08,
              }}
              className="flex gap-4 p-5 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-indigo-500/20 transition-all duration-500"
            >
              <span className="text-indigo-400 font-bold text-sm mt-0.5 shrink-0 font-Montserrat tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-Montserrat font-semibold text-base leading-snug mb-1.5">
                  {adv.title}
                </h4>
                <p className="text-white/60 font-Montserrat font-normal text-sm leading-relaxed text-justify">
                  {adv.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </FadeIn>
  );
}

function IndustrySection({
  industry_applications,
}: {
  industry_applications: NonNullable<BlogPost["industry_applications"]>;
}) {
  return (
    <FadeIn>
      <SectionHeading>{industry_applications.title}</SectionHeading>
      <div className="space-y-5">
        {industry_applications.items.map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{
              duration: 0.5,
              ease: [0.16, 1, 0.3, 1],
              delay: i * 0.08,
            }}
            className="p-5 rounded-xl border border-white/5 bg-white/[0.02]"
          >
            <h4 className="text-white font-Montserrat font-semibold text-base mb-2 flex items-center gap-2">
              <span className="w-0.5 h-4 rounded-full bg-indigo-500/60 shrink-0" />
              {item.industry}
            </h4>
            <p className="text-white/60 font-Montserrat font-normal text-base leading-relaxed pl-[10px] text-justify">
              {item.description}
            </p>
          </motion.div>
        ))}
      </div>
    </FadeIn>
  );
}

function UseCaseExamples({
  examples,
}: {
  examples: NonNullable<BlogPost["use_case_examples"]>;
}) {
  return (
    <FadeIn>
      <SectionHeading>See Augmented Reality in Action</SectionHeading>
      <div className="space-y-4">
        {examples.map((ex, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{
              duration: 0.5,
              ease: [0.16, 1, 0.3, 1],
              delay: i * 0.1,
            }}
            className="flex gap-3 p-4 rounded-xl border border-white/5 bg-white/[0.02]"
          >
            <span className="text-indigo-400 text-lg shrink-0 mt-0.5">
              {i + 1}.
            </span>
            <p className="text-white/70 font-Montserrat font-normal text-sm leading-relaxed text-justify">
              &ldquo;{ex}&rdquo;
            </p>
          </motion.div>
        ))}
      </div>
    </FadeIn>
  );
}

function PassiveCommunicationSection({
  passive,
}: {
  passive: NonNullable<BlogPost["passive_communication"]>;
}) {
  return (
    <FadeIn>
      <SectionHeading>{passive.title}</SectionHeading>
      <p className="text-base md:text-lg text-white/70 font-Montserrat font-normal leading-relaxed mb-8 text-justify">
        {passive.description}
      </p>

      {passive.media_evolution_table && (
        <div className="overflow-x-auto mb-8">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-white/10">
                <th className="py-3 pr-4 text-xs font-Montserrat uppercase tracking-widest text-white/40 font-normal">
                  Era
                </th>
                <th className="py-3 pl-4 text-xs font-Montserrat uppercase tracking-widest text-white/40 font-normal">
                  Impact
                </th>
              </tr>
            </thead>
            <tbody>
              {passive.media_evolution_table.map((row, i) => (
                <tr key={i} className="border-b border-white/5 last:border-0">
                  <td className="py-3 pr-4 text-sm text-white font-Montserrat font-semibold">
                    {row.era}
                  </td>
                  <td className="py-3 pl-4 text-sm text-white/60 font-Montserrat font-normal text-justify">
                    {row.interaction}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {passive.ar_impact && (
        <div className="p-4 md:p-5 rounded-xl border-l-2 border-indigo-500/60 bg-indigo-500/5">
          <p className="text-sm md:text-base text-white/80 font-Montserrat leading-relaxed text-justify">
            {passive.ar_impact}
          </p>
        </div>
      )}
    </FadeIn>
  );
}

function FutureSection({
  future_section,
}: {
  future_section: NonNullable<BlogPost["future_section"]>;
}) {
  return (
    <FadeIn>
      <div className="p-6 md:p-8 rounded-2xl border border-white/10 bg-gradient-to-br from-indigo-500/5 to-transparent">
        <SectionHeading>{future_section.title}</SectionHeading>
        <p className="text-base md:text-lg text-white/70 font-Montserrat font-normal leading-relaxed text-justify">
          {future_section.description}
        </p>
      </div>
    </FadeIn>
  );
}

function CompanySection({
  company,
}: {
  company: NonNullable<BlogPost["company"]>;
}) {
  return (
    <FadeIn>
      <div className="text-center pt-6">
        {company.tagline && (
          <p className="text-base text-indigo-300 font-Montserrat font-semibold ">
            {company.tagline}
          </p>
        )}
      </div>
    </FadeIn>
  );
}

function ReadNextSection({
  read_next,
}: {
  read_next: NonNullable<NonNullable<BlogPost["company"]>["cta"]>["read_next"];
}) {
  if (!read_next) return null;
  const nextPost = blogPosts.find((p) => p.slug === read_next.slug);
  if (!nextPost) return null;

  return (
    <FadeIn>
      <div className="text-sm font-Montserrat text-white/30 uppercase tracking-widest mb-4">
        Read Next
      </div>
      <Link
        to={`/blog/${nextPost.slug}`}
        className="group flex items-start gap-5 p-5 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-indigo-500/30 transition-all duration-500 cursor-pointer focus-visible:outline-2 focus-visible:outline-indigo-500 focus-visible:outline-offset-2"
      >
        <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0">
          <img
            src={nextPost.image}
            alt={nextPost.heading}
            loading="lazy"
            width="80"
            height="80"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] font-Montserrat uppercase tracking-widest text-indigo-400">
              {nextPost.category}
            </span>
            <span className="text-white/20 text-xs">·</span>
            <span className="text-[10px] font-Montserrat text-white/40">
              {nextPost.readTime}
            </span>
          </div>
          <h4 className="text-base md:text-lg font-Montserrat font-semibold text-white/80 group-hover:text-white transition-colors duration-300 leading-tight">
            {read_next.label}
          </h4>
        </div>
        <ArrowRight
          className="text-white/30 group-hover:text-indigo-400 group-hover:translate-x-1 transition-all duration-300 shrink-0 mt-1"
          size={18}
        />
      </Link>
    </FadeIn>
  );
}

export default function BlogDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.slug === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!post) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-transparent">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Blog not found</h1>
          <button
            onClick={() => navigate("/blog")}
            className="text-indigo-400 hover:text-indigo-300 transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-indigo-500"
          >
            Back to blog
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen bg-transparent">
      {/* ── Hero ── */}
      <div className="relative min-h-[55vh] md:min-h-[65vh] w-full flex flex-col justify-between overflow-hidden pt-28 pb-10 md:pb-14 px-6 md:px-12">
        <motion.img
          src={post.image}
          alt={post.heading}
          width="800"
          height="600"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/40" />

        {/* Back button safely below navbar */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-20 w-full max-w-4xl mx-auto mb-8"
        >
          <button
            onClick={() => navigate("/blog")}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/40 dark:bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm hover:bg-white/20 active:scale-95 transition-all duration-300 cursor-pointer shadow-lg"
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </motion.div>

        {/* Heading & Metadata */}
        <div className="relative z-20 w-full max-w-4xl mx-auto mt-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-Montserrat uppercase tracking-widest px-3 py-1 rounded-full border border-white/30 text-white/90 bg-white/10 backdrop-blur-sm">
                {post.category}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-white/80 font-medium">
                <Clock size={13} />
                {post.readTime}
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              {post.heading}
            </h1>
          </motion.div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16 space-y-14">
        {/* Intro */}
        <FadeIn>
          <p className="text-lg md:text-xl text-white/70 font-Montserrat font-normal leading-relaxed text-justify border-l-2 border-indigo-500/50 pl-4">
            {post.intro}
          </p>
        </FadeIn>

        {/* What Is */}
        {post.what_is && <WhatIsSection what_is={post.what_is} />}

        {/* Benefits */}
        {post.benefits && <BenefitsSection benefits={post.benefits} />}

        {/* Why Invest */}
        {post.why_invest && <WhyInvestSection why_invest={post.why_invest} />}

        {/* Use Case Examples */}
        {post.use_case_examples && post.use_case_examples.length > 0 && (
          <UseCaseExamples examples={post.use_case_examples} />
        )}

        {/* Passive Communication */}
        {post.passive_communication && (
          <PassiveCommunicationSection passive={post.passive_communication} />
        )}

        {/* Industry Applications */}
        {post.industry_applications && (
          <IndustrySection industry_applications={post.industry_applications} />
        )}

        {/* Future Section */}
        {post.future_section && (
          <FutureSection future_section={post.future_section} />
        )}

        {/* Company */}
        {post.company && <CompanySection company={post.company} />}

        {/* Read Next */}
        {post.company?.cta?.read_next && (
          <ReadNextSection read_next={post.company.cta.read_next} />
        )}

        {/* Footer Nav */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="pt-8 border-t border-white/10 flex justify-between items-center"
        >
          <button
            onClick={() => navigate("/blog")}
            className="flex items-center gap-2 text-white/50 hover:text-white transition-colors duration-300 cursor-pointer focus-visible:outline-2 focus-visible:outline-indigo-500"
          >
            <ArrowLeft size={16} />
            <span className="font-Montserrat text-sm">Back to Blog</span>
          </button>
          <span className="text-white/20 font-Montserrat text-xs">
            {post.date}
          </span>
        </motion.div>
      </div>
    </main>
  );
}
